// /q/{token} — QR 스캔 착지 (기공소 전용, 휴대폰) — 기획서_QR공정체크 §5-4
// 토큰 → 대상 문서(workOrders | parsedCases) 해석 후 CaseCheckView 하나만 띄운다.
// 치과는 QR을 쓰지 않는다(대표 확정 08-08 — 범위 축소). 로그인 필수(라우트가 보호 구역).
// 대상 문서 읽기 권한(rules)이 최종 방어선 — 남의 기공소 토큰을 열면 문서 읽기에서 거부된다.
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, onSnapshot, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import CaseCheckView from './CaseCheckView';
import { buildCheckPatch } from '../utils/caseChecks';

const S = {
    page: { maxWidth: 480, margin: '0 auto', padding: '18px 16px 40px' },
    head: { fontSize: 13, fontWeight: 700, color: '#64748b', marginBottom: 12 },
    box: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '16px 16px 18px' },
    err: {
        background: '#fdf3f3', border: '1px solid rgba(208,59,59,0.3)', color: '#8a2b2b',
        borderRadius: 10, padding: '14px 16px', fontSize: 13.5, lineHeight: 1.7,
    },
    dim: { fontSize: 13, color: '#94a3b8', padding: '30px 0', textAlign: 'center' },
};

export default function CaseCheckPage({ user }) {
    const { token } = useParams();
    const [state, setState] = useState({ loading: true, error: '', target: null });
    const [entity, setEntity] = useState(null);

    // ① 토큰 해석 (1회)
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                if (!token || token.length < 10) { setState({ loading: false, error: '잘못된 QR입니다.', target: null }); return; }
                const snap = await getDoc(doc(db, 'caseTokens', token));
                if (cancelled) return;
                if (!snap.exists()) { setState({ loading: false, error: '등록되지 않았거나 회수된 QR입니다.', target: null }); return; }
                const t = snap.data();
                const exp = t.expiresAt && (typeof t.expiresAt.toDate === 'function' ? t.expiresAt.toDate() : new Date(t.expiresAt));
                if (t.revokedAt || (exp && exp.getTime() < Date.now())) {
                    setState({ loading: false, error: '기한이 지난 QR입니다. 의뢰서를 다시 인쇄하면 새 QR이 담깁니다.', target: null });
                    return;
                }
                if (!t.target || !t.target.id) { setState({ loading: false, error: 'QR 정보가 손상되었습니다.', target: null }); return; }
                setState({ loading: false, error: '', target: t.target });
            } catch (e) {
                console.error('QR 토큰 해석 실패:', e?.code || e?.message);
                if (!cancelled) setState({ loading: false, error: 'QR을 확인하지 못했습니다. 네트워크를 확인해 주세요.', target: null });
            }
        })();
        return () => { cancelled = true; };
    }, [token]);

    // ② 대상 문서 실시간 구독 — 체크 저장 후 화면이 즉시 따라온다
    useEffect(() => {
        if (!state.target) return undefined;
        const col = state.target.origin === 'case' ? 'parsedCases' : 'workOrders';
        const unsub = onSnapshot(doc(db, col, state.target.id), (snap) => {
            setEntity(snap.exists() ? { id: snap.id, ...snap.data(), ...(col === 'parsedCases' ? { _isParsed: true } : {}) } : null);
        }, (e) => {
            // 남의 기공소 토큰 = 여기서 permission-denied — 내용은 아무것도 노출되지 않는다
            console.error('QR 대상 조회 실패:', e?.code);
            setEntity(null);
            setState(prev => ({ ...prev, error: '이 케이스를 볼 권한이 없습니다. 우리 기공소 계정으로 로그인했는지 확인해 주세요.' }));
        });
        return () => unsub();
    }, [state.target]);

    const onToggle = async (key, next) => {
        const patch = buildCheckPatch(key, next, entity);
        if (!patch) throw new Error('허용되지 않은 체크 항목입니다');
        const col = state.target.origin === 'case' ? 'parsedCases' : 'workOrders';
        await updateDoc(doc(db, col, state.target.id), patch);   // 실패는 CaseCheckView가 받아 롤백
    };

    return (
        <div style={S.page}>
            <div style={S.head}>{'공정 체크'}</div>
            {state.loading && <div style={S.dim}>{'QR 확인 중…'}</div>}
            {!state.loading && state.error && <div style={S.err}>{state.error}</div>}
            {!state.loading && !state.error && state.target && !entity && <div style={S.dim}>{'케이스 불러오는 중…'}</div>}
            {!state.loading && !state.error && entity && (
                <div style={S.box}>
                    <CaseCheckView entity={entity} onToggle={onToggle} />
                </div>
            )}
        </div>
    );
}
